/**
 * E2E renderer probe for add_position_preset / recall_position_preset.
 *
 * WHY: this PR adds the two position-preset canonical events, and the PostHog CI
 * receipt selector runs in degrade mode (emit-site coverage INACTIVE until
 * ENG-4514), so a change under the analytics source root that selects no receipt
 * is a red analytics-path tripwire. The events therefore need a real receipt.
 *
 * The probe drives the PRODUCTION closure, `presetActionReceipt` from
 * control/preset/analytics.ts, so the receipt proves the actual emit path: the
 * frozen device/model/organization context captured when the operator acts, the
 * generated action_id, and the once-only guard. It never fabricates a track() call
 * and never supplies event properties itself.
 *
 * `run` is the only entry point, because seeding and cleanup must not be separate
 * round trips: the Electron e2e fixture is shared and does not reset the depot, so
 * a spec that failed between two calls would leave a device behind for every later
 * spec. Seed, emit and release happen inside ONE try/finally here, so the seam is
 * released on success AND on failure. Seeding itself lives in preset-probe-seed.ts
 * (unit-tested there) and refuses rather than inventing an organization.
 *
 * Mirrors media-controls-telemetry-probe.ts, same IRIS_E2E_TEST gate.
 * Per rule: stay-in-scope + investigate-before-adding (ai.knowledge/global).
 * Per rule: reference-rules-in-comments (ai.knowledge/global).
 */
import { presetActionReceipt } from '#~/control/preset/analytics.js';
import { depot } from '#~/depot/index.js';
import { createPresetProbeSeam, runWithSeed } from './preset-probe-seed.js';
import type { PresetProbeRunReceipt } from './preset-probe-seed.js';
import { castId } from '@studio/models';
import type { Device } from '@studio/models/device';

interface PresetActionTelemetryProbe {
  run(
    deviceId: string,
    action: 'save' | 'recall',
    options?: { twice?: boolean }
  ): PresetProbeRunReceipt;
}

const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

if (isE2eEnabled()) {
  const seam = createPresetProbeSeam(() => depot.getState().produce);

  const probe: PresetActionTelemetryProbe = {
    run(deviceId, action, options) {
      return runWithSeed(seam, deviceId, () => {
        const accepted = presetActionReceipt(castId<Device['id']>(deviceId), action);
        accepted();
        if (options?.twice === true) {
          // The SAME closure again: the once-only guard must swallow it.
          accepted();
        }
      });
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisPresetActionTelemetry = probe;
}
